/**
 * API 服务与流式响应工具
 */
import axios from 'axios';
import { ConfigData, OutlineData, OutlineItem, OutlineMode } from '../types';
import {
  getAccessToken,
  invalidateAuthSession,
} from './authStorage';

/** 后端统一响应格式 */
type ApiResponse<T = null> = { code: number; data: T; msg: string };

/** 分页响应数据结构（参照 pear-admin-flask 分页思路：total + list + 分页参数） */
export interface PaginatedData<T> {
  total: number;
  list: T[];
  page: number;
  size: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || window.location.origin;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // 不再自动解包，response.data 保持后端统一格式 {code, data, msg}
    // 业务数据通过 response.data.data 获取
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      invalidateAuthSession();
    }
    return Promise.reject(error);
  }
);

export interface LoginResponse {
  access_token: string;
  token_type: string;
  member_id: number;
  username: string;
  /** 是否需要补充绑定（用户名密码或微信） */
  need_bind?: boolean;
  /** 需要绑定的类型：none / username_password / wechat */
  bind_type?: 'none' | 'username_password' | 'wechat';
}

export interface CurrentUser {
  id: number;
  username: string;
  available_words: number;
  balance: number | string;
  status: number;
  create_time: string | null;
  /** 是否需要补充绑定（用户名密码或微信） */
  need_bind?: boolean;
  /** 需要绑定的类型：none / username_password / wechat */
  bind_type?: 'none' | 'username_password' | 'wechat';
}

export interface ProjectSummary {
  id: number;
  project_name: string;
  overview: string;
  requirements: string;
  status: number;
  flow_status: 0 | 10 | 20 | 25 | 30 | 40;
  create_time: number | string | null;
  update_time: number | string | null;
}

export interface ProjectProgress {
  project_id: number;
  progress_percent: number;
  outline_leaf_total: number;
  generated_leaf_count: number;
  failed_leaf_count: number;
  actual_word_count: number;
}

export interface ProjectSettings {
  writing_style?: string | null;
  table_density?: string | null;
  tender_type?: string | null;
  company_name?: string | null;
  company_intro?: string | null;
  company_industry?: string | null;
  bid_focus?: string | null;
}

export interface ProjectSettingsDetail extends ProjectSettings {
  version_no: number;
  expected_total_words: number;
  company_scale: string;
  expected_pages: number;
  layout_style: string;
  supplier_role: string;
  planned_duration: string;
  plan_duration_days: number | null;
  plan_duration_text: string | null;
}

export interface ProjectFile {
  id: number;
  file_name: string;
  file_path?: string;
  file_type?: string;
  file_size: number;
  create_time?: number | string | null;
}

export type ProjectFileRole =
  | 'tender'
  | 'goods_list'
  | 'service_list'
  | 'bill_of_quantities'
  | 'other_list'
  | 'list'
  | 'template'
  | 'writing_material'
  | 'reference_bid'
  | 'drawing';

export interface DurableProjectFile {
  id: number;
  storage_file_id: number | null;
  file_name: string;
  file_role: ProjectFileRole;
  file_size: number;
  file_hash: string;
  mime_type: string;
  parse_status: number;
  parse_progress: number;
  parse_error_code: string | null;
  parse_error_message: string | null;
  parser_version: string | null;
  page_count: number | null;
  create_time: number;
  update_time: number;
}

export type FileSourceMode = 'file' | 'paste' | '';

export interface FileSourceModes {
  tender: FileSourceMode;
  list: FileSourceMode;
  template: FileSourceMode;
  writing_material: FileSourceMode;
  reference_bid: FileSourceMode;
  expand: FileSourceMode;
  drawing: FileSourceMode;
}

export interface ProjectFiles {
  tender: ProjectFile[];
  list: ProjectFile[];
  template: ProjectFile[];
  writing_material: ProjectFile[];
  reference_bid: ProjectFile[];
  expand: ProjectFile[];
  drawing: ProjectFile[];
}

export interface ProjectDetail extends ProjectSummary, ProjectSettings {
  expected_total_words?: number;
  company_scale?: string;
  version_no?: number;
  analysis_version?: number;
  file_source_modes: FileSourceModes;
  files: ProjectFiles;
  list_pasted_text?: string;
}

export interface PersistedTaskItem {
  id: number;
  item_type: string;
  target_id: string;
  status: number;
  status_name: string;
  sort_order: number;
  progress_current: number;
  progress_total: number;
  error_code: string | null;
  error_message: string | null;
  content_plan?: Record<string, unknown>;
  result_snapshot?: Record<string, unknown>;
}

export interface PersistedTaskSnapshot {
  id: number;
  task_no: string;
  project_id: number;
  task_type: string;
  status: number;
  status_name: string;
  phase: string | null;
  total_units: number;
  completed_units: number;
  progress_percent: number;
  success_units: number;
  failed_units: number;
  cancelled_units: number;
  error_code: string | null;
  error_message: string | null;
  items?: PersistedTaskItem[];
  request_snapshot?: Record<string, unknown>;
  runtime_snapshot?: Record<string, unknown>;
}

export interface AnalysisRequirement {
  id?: number;
  requirement_code: string;
  title: string;
  description: string;
  score_text: string;
  source_location: string;
  source_type: string;
  sort_order: number;
}

export interface DocumentAnalysisSnapshot {
  id: number;
  version_no: number;
  overview: string;
  requirements: string;
  rejection_terms: Array<Record<string, unknown> | string>;
  delivery_requirements: Array<Record<string, unknown> | string>;
  tender_items: GoodsRow[];
  company_items: GoodsRow[];
  source_file_ids: number[];
  analysis_mode: 'file' | 'paste';
  confirmed: boolean;
  prompt_version: string | null;
  model_name: string | null;
  create_time: number;
}

export interface DocumentAnalysisState {
  project_version: number;
  analysis: DocumentAnalysisSnapshot | null;
  task: PersistedTaskSnapshot | null;
}

export interface DurableOutlineNode {
  id: string;
  title: string;
  description: string;
  merged_writing_idea?: string;
  expected_word_count: number;
  actual_word_count?: number;
  source_requirement_id: string;
  source_requirement_title: string;
  generation_status?: number | null;
  content_version_no?: number;
  children: DurableOutlineNode[];
}

export interface OutlineWorkspaceState {
  project_id: number;
  project_version: number;
  outline_version: number;
  confirmed: boolean;
  outline: DurableOutlineNode[];
  overview: string;
  requirements: string;
}

export interface OutlineVersionRecord {
  id: number;
  version_no: number;
  source_type: string;
  created_by: number;
  create_time: number;
}

export interface OutlineOperationTemplate {
  code: 'compliance' | 'structure' | 'detail' | 'custom';
  name: string;
  instruction: string;
}

export interface TemplateItem {
  value: string;
  label: string;
  content?: string;
  is_default: boolean;
}

export interface OutlineAIOperation {
  id: number;
  project_id: number;
  operation_type: 'optimize' | 'expand' | 'manual_idea' | 'ai_optimize_idea' | 'ai_generate_idea';
  target_type: 'outline';
  target_ids: string[];
  instruction: string;
  style: string;
  base_version: number;
  result_snapshot: {
    outline?: DurableOutlineNode[];
    optimized_idea?: string;
    generated_idea?: string;
  };
  status: number;
  task_id: number | null;
  create_time: number;
  update_time: number;
}

export interface ReferenceBid {
  id: number;
  project_id: number;
  project_name: string;
  file_name: string;
  file_size: number;
  file_hash: string;
  mime_type: string;
  parse_status: number;
  page_count: number | null;
  create_time: number;
  update_time: number;
}

export interface ReferenceBidPreview {
  id: number;
  project_id: number;
  project_name: string;
  file_name: string;
  file_hash: string;
  offset: number;
  end_offset: number;
  total_characters: number;
  has_more: boolean;
  content: string;
}

export interface ChapterReferenceSelection {
  outline_id: string;
  reference_file_ids: number[];
  reference_fragments: Array<{
    file_id: number;
    start_offset: number;
    end_offset: number;
  }>;
  reference_weight: 'low' | 'medium' | 'high';
}

export interface ContentChapter {
  outline_id: string;
  parent_id?: string | null;
  title: string;
  parent_titles: string[];
  level: number;
  sort_order: number;
  expected_word_count: number;
  actual_word_count: number;
  content_version_no: number;
  generation_status: number;
  generation_status_name: string;
  last_error_code: string | null;
  last_error_message: string | null;
  has_content: boolean;
  content: string;
  is_image_rich?: boolean;
  update_time: number;
  children?: ContentChapter[];
}

export interface ContentDashboard {
  project_id: number;
  project_version: number;
  outline_version: number;
  content_version: number;
  flow_status: number;
  writing_style?: string;
  available_styles?: Array<{ key: string; label: string; description?: string }>;
  capabilities: {
    generation_enabled: boolean;
    generation_disabled_reason: string;
  };
  summary: {
    total_chapters: number;
    completed_chapters: number;
    failed_chapters: number;
    actual_word_count: number;
    progress_percent: number;
  };
  quota: {
    initialized: boolean;
    available_words: number;
    frozen_words: number;
    used_words: number;
    nearest_expire_at: number;
    version_no: number;
    status: number;
  };
  latest_task: PersistedTaskSnapshot | null;
  chapters: ContentChapter[];
  chapter_tree?: ContentChapter[];
}

export interface WordExportRecord {
  id: number;
  project_id: number;
  task_id: number | null;
  export_type: string;
  outline_version: number;
  content_version: number;
  storage_file_id: number | null;
  file_name: string;
  file_size: number;
  file_hash: string | null;
  status: number;
  status_name: string;
  error_code: string | null;
  error_message: string | null;
  expire_at: number;
  downloadable: boolean;
  download_url: string | null;
  create_time: number;
  update_time: number;
}

export type GoodsRow = Record<string, unknown>;

export interface GoodsMatchingState {
  analysis_result_id: number | null;
  analysis_version: number;
  source_file_ids: Array<number | string>;
  create_time: number;
  schema_version: 1;
  tender_items: GoodsRow[];
  company_items: GoodsRow[];
  matches: GoodsRow[];
  confirmed: boolean;
  base_analysis_version: number;
  task: PersistedTaskSnapshot | null;
}

export interface FileUploadResponse {
  file_content?: string;
  old_outline?: string;
}

export interface AnalysisRequest {
  file_content: string;
  analysis_type: 'overview' | 'requirements';
}

export interface OutlineRequest extends ProjectSettings {
  overview: string;
  requirements: string;
  mode?: OutlineMode;
  uploaded_expand?: boolean;
  old_outline?: string;
  old_document?: string;
  optimization_instruction?: string;
  optimization_style?: string;
}

export interface ChapterContentRequest extends ProjectSettings {
  chapter: OutlineItem;
  parent_chapters?: OutlineItem[];
  sibling_chapters?: OutlineItem[];
  project_overview: string;
  max_length?: number;
}

export interface WordExportRequest extends ProjectSettings {
  project_name?: string;
  outline: OutlineItem[];
}

export interface StreamEvent {
  type?: 'chunk' | 'progress' | 'result' | 'done' | 'end';
  chunk?: string;
  data?: string;
  outline?: OutlineData;
  error?: boolean;
  message?: string;
}

export interface NamedSseEvent<T> {
  id?: string;
  event: string;
  retry?: number;
  data: T;
}

export interface TaskEventPayload {
  task_id: number;
  task_status: number;
  completed_units: number;
  total_units: number;
  progress_percent: number;
  event_id?: string;
  task: PersistedTaskSnapshot;
}

export interface WorkspaceSummary {
  packages: Array<{
    id: number;
    fees_type: string;
    fees_name: string;
    fees_money: string;
    add_times: number;
    remark: string;
  }>;
  payments: Array<{
    id: number;
    payment_type: string;
    amount: string;
    payment_method: string;
    payment_status: number;
    payment_time: number;
    remark: string | null;
    create_time: number;
  }>;
  invoices: Array<{
    id: number;
    payment_id: number;
    company_name: string;
    money: string;
    status: number;
    img: string | null;
    create_time: number;
    update_time: number;
  }>;
  usage_records: Array<{
    id: string;
    record_type: 'quota' | 'ai_operation';
    operation: string;
    amount: number | null;
    available_after: number | null;
    status: number;
    remark: string | null;
    create_time: number;
  }>;
  messages: Array<{
    id: string;
    type: string;
    title: string;
    content: string;
    create_time: number;
    project_id: number;
  }>;
  capabilities: {
    payment_enabled: boolean;
    invoice_application_enabled: boolean;
    wechat_login_enabled: boolean;
    payment_reason: string;
    invoice_reason: string;
    wechat_login_reason: string;
  };
}

/** 可购买套餐 */
export interface WorkspacePackage {
  id: number;
  fees_type: string;
  fees_name: string;
  fees_money: string;
  add_times: number;
  remark: string;
}

/** 支付记录 */
export interface WorkspacePayment {
  id: number;
  payment_type: string;
  package_name: string;
  amount: string;
  payment_method: string;
  payment_status: number;
  payment_time: number;
  remark: string | null;
  invoice_status: number | null;
  invoice_img: string;
  create_time: number;
}

/** 发票记录 */
export interface WorkspaceInvoice {
  id: number;
  payment_id: number;
  company_name: string;
  money: string;
  status: number;
  img: string | null;
  create_time: number;
  update_time: number;
}

/** 使用/消耗记录 */
export interface WorkspaceUsageRecord {
  id: string;
  record_type: 'quota' | 'ai_operation';
  operation: string;
  amount: number | null;
  available_after: number | null;
  status: number;
  remark: string | null;
  create_time: number;
}

export const getSseTextChunk = (event: StreamEvent): string | undefined =>
  event.chunk ?? (event.type === 'chunk' ? event.data : undefined);

const translateUserMessage = (
  message: string | null | undefined,
  fallback: string
): string => {
  if (!message) {
    return fallback;
  }

  const knownMessages: Record<string, string> = {
    'Network Error': '网络连接失败，请检查服务是否已启动',
    'Failed to fetch': '网络请求失败，请检查网络连接',
    'Not authenticated': '登录状态已失效，请重新登录',
    'Could not validate credentials': '登录状态已失效，请重新登录',
    Unauthorized: '没有访问权限，请重新登录',
    'Not Found': '请求的功能不存在',
    'Internal Server Error': '服务器处理失败，请稍后重试',
    'Unexpected end of JSON input': '服务器返回的数据不完整',
  };
  const trimmedMessage = message.trim();

  if (knownMessages[trimmedMessage]) {
    return knownMessages[trimmedMessage];
  }
  if (/timeout/i.test(trimmedMessage)) {
    return '请求超时，请稍后重试';
  }

  const statusMatch = trimmedMessage.match(/^Request failed with status code (\d+)$/);
  return statusMatch
    ? `请求失败（状态码：${statusMatch[1]}）`
    : trimmedMessage;
};

const formatErrorDetail = (detail: unknown): string | null => {
  if (!detail) {
    return null;
  }

  if (typeof detail === 'string') {
    return translateUserMessage(detail, '请求失败');
  }

  if (Array.isArray(detail)) {
    const lines = detail
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (item && typeof item === 'object') {
          const maybeItem = item as { loc?: Array<string | number>; msg?: string };
          const path = maybeItem.loc?.join(' -> ');
          const message = translateUserMessage(maybeItem.msg, '参数校验失败');
          return path ? `${path}: ${message}` : message;
        }

        return null;
      })
      .filter((line): line is string => Boolean(line));

    return lines.length > 0 ? lines.join('；') : null;
  }

  if (detail && typeof detail === 'object') {
    const maybeDetail = detail as { detail?: unknown; message?: string };
    return formatErrorDetail(maybeDetail.detail) || maybeDetail.message || JSON.stringify(detail);
  }

  return String(detail);
};

export const getErrorMessage = (error: unknown, fallback = '请求失败'): string => {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    const detail = responseData?.detail;
    const message = responseData?.message;
    const msg = responseData?.msg;
    return formatErrorDetail(detail)
      || translateUserMessage(msg || message || error.message, fallback);
  }

  if (error instanceof Error) {
    return translateUserMessage(error.message, fallback);
  }

  return fallback;
};

const ensureResponseOk = async (response: Response, fallback: string): Promise<Response> => {
  if (response.ok) {
    return response;
  }

  if (response.status === 401) {
    invalidateAuthSession();
  }

  try {
    const data = await response.json();
    throw new Error(
      formatErrorDetail(data.detail)
      || translateUserMessage(data.msg || data.message, fallback)
    );
  } catch (error) {
    if (error instanceof Error && error.message !== 'Unexpected end of JSON input') {
      throw error;
    }
  }

  const text = await response.text();
  throw new Error(translateUserMessage(text, fallback));
};

export const readSseStream = async (
  response: Response,
  onEvent: (event: StreamEvent) => void,
  fallbackMessage: string
): Promise<void> => {
  await ensureResponseOk(response, fallbackMessage);

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('无法读取响应流');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      reader.releaseLock();
      // #region debug-point E:stream-closed
      if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
        void fetch('http://127.0.0.1:7780/event', {
          method: 'POST',
          body: JSON.stringify({
            sessionId: 'regression-fixes-validation',
            runId: 'post-fix',
            hypothesisId: 'E',
            location: 'frontend/src/services/api.ts:readSseStream',
            msg: '[DEBUG] SSE body closed and reader lock released',
            data: { bufferedCharacters: buffer.length },
            ts: Date.now(),
          }),
        }).catch(() => undefined);
      }
      // #endregion
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';

    for (const eventBlock of events) {
      const dataLine = eventBlock
        .split('\n')
        .find((line) => line.startsWith('data: '));

      if (!dataLine) {
        continue;
      }

      const data = dataLine.slice(6);
      if (data === '[DONE]') {
        reader.releaseLock();
        // #region debug-point E:legacy-done
        if (process.env.REACT_APP_DEBUG_EVENTS === 'true' && typeof fetch === 'function') {
          void fetch('http://127.0.0.1:7780/event', {
            method: 'POST',
            body: JSON.stringify({
              sessionId: 'regression-fixes-validation',
              runId: 'post-fix',
              hypothesisId: 'E',
              location: 'frontend/src/services/api.ts:readSseStream',
              msg: '[DEBUG] Legacy done marker released reader lock',
              data: {},
              ts: Date.now(),
            }),
          }).catch(() => undefined);
        }
        // #endregion
        return;
      }

      let event: StreamEvent | null = null;
      try {
        event = JSON.parse(data) as StreamEvent;
      } catch {
        // 忽略非法片段，等待后续完整数据
        continue;
      }

      onEvent(event);
    }
  }
};

export const readNamedSseStream = async <T>(
  response: Response,
  onEvent: (event: NamedSseEvent<T>) => void,
  fallbackMessage: string
): Promise<void> => {
  await ensureResponseOk(response, fallbackMessage);

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('无法读取响应流');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  const consumeBlock = (block: string) => {
    let id: string | undefined;
    let event = 'message';
    let retry: number | undefined;
    const dataLines: string[] = [];

    block.replace(/\r/g, '').split('\n').forEach((line) => {
      if (!line || line.startsWith(':')) return;
      const separator = line.indexOf(':');
      const field = separator >= 0 ? line.slice(0, separator) : line;
      const rawValue = separator >= 0 ? line.slice(separator + 1) : '';
      const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;
      if (field === 'id') id = value;
      if (field === 'event' && value) event = value;
      if (field === 'retry' && /^\d+$/.test(value)) retry = Number(value);
      if (field === 'data') dataLines.push(value);
    });

    if (dataLines.length === 0) return;
    try {
      onEvent({
        id,
        event,
        retry,
        data: JSON.parse(dataLines.join('\n')) as T,
      });
    } catch {
      // Ignore one malformed event without terminating the task subscription.
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() || '';
      blocks.forEach(consumeBlock);
    }
    buffer += decoder.decode();
    if (buffer.trim()) consumeBlock(buffer);
  } finally {
    reader.releaseLock();
  }
};

export const collectSseText = async (
  response: Response,
  onText?: (fullText: string, chunk: string) => void,
  fallbackMessage = '流式请求失败'
): Promise<string> => {
  let fullText = '';

  await readSseStream(
    response,
    (event) => {
      if (event.error) {
        throw new Error(event.message || fallbackMessage);
      }

      const chunk = getSseTextChunk(event);
      if (!chunk) {
        return;
      }

      fullText += chunk;
      onText?.(fullText, chunk);
    },
    fallbackMessage
  );

  return fullText;
};

const postJson = (path: string, data: unknown) => {
  const token = getAccessToken();
  return fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
  });
};

export const authApi = {
  login: (username: string, password: string) => {
    const formData = new URLSearchParams();
    formData.set('username', username);
    formData.set('password', password);

    return api.post<ApiResponse<LoginResponse>>('/api/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
  getCurrentUser: () => api.get<ApiResponse<CurrentUser>>('/api/auth/me'),
  getWechatQrcode: () =>
    api.get<ApiResponse<{ code_url: string; ticket: string }>>(
      '/api/auth/wechat/qrcode'
    ),
  pollWechatLogin: (ticket: string) =>
    api.post<ApiResponse<{ scanned: boolean; ciphertext: string | null }>>(
      '/api/auth/wechat/poll',
      { ticket }
    ),
  wechatLoginByCiphertext: (ciphertext: string) =>
    api.post<ApiResponse<LoginResponse & { openid: string; need_bind_info?: boolean }>>(
      '/api/auth/wechat/login',
      { ciphertext }
    ),

  /** zbapi JWT 模式：zbapi 平台升级后直接返回 JWT，用它换签我们的 JWT */
  wechatLoginByJwt: (zbapi_jwt: string) =>
    api.post<ApiResponse<LoginResponse & { openid: string; need_bind_info?: boolean }>>(
      '/api/auth/wechat/login-jwt',
      { zbapi_jwt }
    ),

  /** 微信新用户首次绑定用户名密码（bind-info 接口） */
  bindInfo: (username: string, password: string) => {
    const formData = new URLSearchParams();
    formData.set('username', username);
    formData.set('password', password);
    return api.post<ApiResponse<{ member_id: number; username: string }>>(
      '/api/auth/bind-info',
      formData,
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    );
  },

  /** 普通账号绑定微信（bind-wechat 接口） */
  bindWechat: (zbapi_jwt: string) =>
    api.post<ApiResponse<{
      member_id: number;
      username: string;
      openid: string;
      need_bind: boolean;
      bind_type: 'none' | 'username_password' | 'wechat';
    }>>('/api/auth/bind-wechat', { zbapi_jwt }),
};

export const workspaceApi = {
  summary: () =>
    api.get<ApiResponse<WorkspaceSummary>>(
      '/api/workspace/summary'
    ),
  // 可购买套餐列表（分页）
  packages: (page = 1, size = 10) =>
    api.get<ApiResponse<PaginatedData<WorkspacePackage>>>(
      '/api/workspace/packages',
      { params: { page, size } }
    ),
  // 当前用户支付记录（分页）
  payments: (page = 1, size = 10) =>
    api.get<ApiResponse<PaginatedData<WorkspacePayment>>>(
      '/api/workspace/payments',
      { params: { page, size } }
    ),
  // 当前用户发票记录（分页）
  invoices: (page = 1, size = 10) =>
    api.get<ApiResponse<PaginatedData<WorkspaceInvoice>>>(
      '/api/workspace/invoices',
      { params: { page, size } }
    ),
  // 当前用户使用/消耗记录（分页）
  usageRecords: (page = 1, size = 10) =>
    api.get<ApiResponse<PaginatedData<WorkspaceUsageRecord>>>(
      '/api/workspace/usage-records',
      { params: { page, size } }
    ),
  // 获取解读文件列表
  interpretationList: (roleGroup: 'tender' | 'list' | 'drawing', page = 1, size = 10) =>
    api.get<ApiResponse<PaginatedData<InterpretationFileItem>>>(
      '/api/workspace/interpretation-list',
      { params: { role_group: roleGroup, page, size } }
    ),
};

export interface InterpretationFileItem {
  id: number;
  project_id: number;
  project_name: string;
  file_name: string;
  file_role: string;
  parse_status: number | null;
  create_time: number;
  create_time_str: string;
}

export type InterpretationRoleGroup = 'tender' | 'list' | 'drawing';

export interface PlanItem {
  id: number;
  fees_type: string;
  fees_name: string;
  fees_money: string;
  add_times: number;
  remark: string;
  purchasable: boolean;
  policy?: {
    quota_words: number;
    valid_days: number;
  };
}

export interface PlanListResponse {
  items: PlanItem[];
  payment_enabled: boolean;
  payment_reason: string | null;
}

export interface QuotaAccount {
  member_id: number;
  available_words: number;
  frozen_words: number;
  used_words: number;
  version_no: number;
  nearest_expire_at: number;
  status: number;
  initialized: boolean;
}

export interface QuotaLedgerItem {
  id: number;
  operation: string;
  amount_words: number;
  available_after: number;
  source_type: string;
  source_id: number | null;
  business_key: string;
  expire_at: number;
  remark: string;
  create_time: number;
}

export interface PaymentListItem {
  id: number;
  payment_no: string;
  fee_id: number;
  amount: string;
  payment_method: string;
  payment_status: number;
  payment_status_name: string;
  payment_time: number | null;
  pay_no: string;
  idempotency_key: string;
  benefit: {
    quota_words: number;
    valid_days: number;
    granted: boolean;
  } | null;
  create_time: number;
  update_time: number;
}

export interface PaymentDetail extends PaymentListItem {
  fee_name: string;
  out_trade_no: string;
}

export interface PaymentCreateResult {
  id: number;
  payment_no: string;
  amount: string;
  payment_status: number;
  pay_no: string;
  gateway: {
    pay_id: string;
    code_url?: string;
    [key: string]: unknown;
  };
}

export interface PaymentStatusResult {
  id: number;
  payment_status: number;
  payment_status_name: string;
  payment_time: number | null;
  benefit: {
    quota_words: number;
    valid_days: number;
    granted: boolean;
  } | null;
}

export interface InvoiceEligiblePayment {
  id: number;
  payment_no: string;
  amount: string;
  payment_time: number;
  fee_name: string;
  already_invoiced: boolean;
}

export interface InvoiceListItem {
  id: number;
  payment_id: number;
  company_name: string;
  taxation: string;
  money: string;
  status: number;
  status_name: string;
  img: string | null;
  create_time: number;
  update_time: number;
}

export interface InvoiceDetail extends InvoiceListItem {
  name: string;
  spece: string;
  unit: string;
  number: string;
  univalent: string;
  remark: string;
}

export interface RefundResult {
  refund_id: number;
  payment_id: number;
  refund_money: string;
  refund_reason: string;
  status: number;
  status_name: string;
  create_time: number;
}

export const commerceApi = {
  listPlans: () =>
    api.get<ApiResponse<PlanListResponse>>('/api/plan'),
  getQuota: () =>
    api.get<ApiResponse<QuotaAccount>>('/api/quota'),
  listQuotaLedger: (page = 1, pageSize = 20) =>
    api.get<ApiResponse<{ items: QuotaLedgerItem[]; total: number; page: number; page_size: number }>>(
      '/api/quota/ledger',
      { params: { page, page_size: pageSize } }
    ),
  createPayment: (data: {
    fee_id: number;
    classify: 'NATIVE' | 'JSAPI';
    openid?: string;
    idempotency_key: string;
  }) =>
    api.post<ApiResponse<PaymentCreateResult>>(
      '/api/payment',
      data
    ),
  listPayments: (page = 1, pageSize = 20) =>
    api.get<ApiResponse<{ items: PaymentListItem[]; total: number; page: number; page_size: number }>>(
      '/api/payment',
      { params: { page, page_size: pageSize } }
    ),
  getPayment: (paymentId: number) =>
    api.get<ApiResponse<PaymentDetail>>(
      `/api/payment/${paymentId}`
    ),
  getPaymentStatus: (paymentId: number) =>
    api.get<ApiResponse<PaymentStatusResult>>(
      `/api/payment/${paymentId}/status`
    ),
  createRefund: (data: {
    payment_id: number;
    refund_money: string;
    refund_reason: string;
  }) =>
    api.post<ApiResponse<RefundResult>>('/api/refund', data),
  listInvoiceEligiblePayments: () =>
    api.get<ApiResponse<{ items: InvoiceEligiblePayment[]; application_enabled: boolean; application_reason: string | null }>>(
      '/api/invoice/eligible-payments'
    ),
  createInvoice: (data: {
    payment_id: number;
    company_name: string;
    taxation: string;
    name?: string;
    spece?: string;
    unit?: string;
    number?: string;
    univalent?: string;
    remark?: string;
  }) =>
    api.post<ApiResponse<InvoiceDetail>>('/api/invoice', data),
  listInvoices: (page = 1, pageSize = 20) =>
    api.get<ApiResponse<{ items: InvoiceListItem[]; total: number; page: number; page_size: number }>>(
      '/api/invoice',
      { params: { page, page_size: pageSize } }
    ),
  getInvoice: (invoiceId: number) =>
    api.get<ApiResponse<InvoiceDetail>>(
      `/api/invoice/${invoiceId}`
    ),
};

export const projectApi = {
  list: (page = 1, size = 10) =>
    api.get<ApiResponse<PaginatedData<ProjectSummary>>>('/api/project/list', {
      params: { page, size },
    }),
  progressList: (page = 1, size = 10) =>
    api.get<ApiResponse<PaginatedData<ProjectProgress>>>(
      '/api/project/progress/list',
      { params: { page, size } }
    ),
  detail: (projectId: number) =>
    api.get<ApiResponse<ProjectDetail>>(`/api/project/${projectId}`),
  settings: (projectId: number) =>
    api.get<ApiResponse<ProjectSettings & { expected_total_words: number }>>(`/api/project/${projectId}/settings`),
  settingsDetail: (projectId: number) =>
    api.get<ApiResponse<ProjectSettingsDetail>>(
      `/api/project/${projectId}/settings/detail`
    ),
  create: (data: {
    project_id?: number;
    project_name: string;
    overview?: string;
    requirements?: string;
    expected_total_words: number;
  }) =>
    api.post<ApiResponse<{ project_id: number }>>(
      '/api/project/create',
      data
    ),
  updateSettings: (
    projectId: number,
    data: ProjectSettings & { expected_total_words?: number }
  ) =>
    api.put<ApiResponse<null>>(
      `/api/project/${projectId}/settings`,
      data
    ),
  updateSettingsDetail: (
    projectId: number,
    data: ProjectSettings & {
      base_version_no: number;
      expected_total_words?: number;
      company_scale?: string;
      expected_pages?: number;
      layout_style?: string;
      supplier_role?: string;
      planned_duration?: string;
      plan_duration_days?: number | null;
      plan_duration_text?: string | null;
    }
  ) =>
    api.put<ApiResponse<ProjectSettingsDetail>>(`/api/project/${projectId}/settings/detail`, data),
  update: (
    projectId: number,
    data: {
      project_name?: string;
      overview?: string;
      requirements?: string;
      status?: number;
      flow_status?: number;
    }
  ) =>
    api.put<ApiResponse<null>>(
      `/api/project/${projectId}`,
      data
    ),
  delete: (projectId: number) =>
    api.delete<ApiResponse<null>>(
      `/api/project/${projectId}`
    ),
  getFileContent: (projectId: number, fileId: number) =>
    api.get<ApiResponse<{
      id: number;
      file_name: string;
      file_type: string;
      content: string;
      create_time: number | string | null;
      update_time: number | string | null;
    }>>(`/api/project/${projectId}/files/${fileId}/content`),
  deleteFile: (projectId: number, fileId: number) =>
    api.delete<ApiResponse<null>>(
      `/api/project/${projectId}/files/${fileId}`
    ),
  getOutline: (projectId: number) =>
    api.get<ApiResponse<OutlineItem[]>>(
      `/api/project/${projectId}/outline`
    ),
  updateFlowStatus: (
    projectId: number,
    flowStatus: ProjectSummary['flow_status']
  ) =>
    api.put<ApiResponse<{ flow_status: ProjectSummary['flow_status'] }>>(
      `/api/project/${projectId}/flow-status`,
      { flow_status: flowStatus }
    ),
  updateOutlineContent: (projectId: number, outlineId: string, content: string) =>
    api.put<ApiResponse<null>>(
      `/api/project/outline/${encodeURIComponent(outlineId)}/content`,
      { content, project_id: projectId }
    ),
};

export const projectFileApi = {
  list: (projectId: number) =>
    api.get<ApiResponse<DurableProjectFile[]>>(
      `/api/project/${projectId}/files`
    ),
  upload: (
    projectId: number,
    file: File,
    fileRole: ProjectFileRole,
    idempotencyKey: string
  ) => {
    const formData = new FormData();
    formData.set('file', file);
    formData.set('file_role', fileRole);
    formData.set('idempotency_key', idempotencyKey);
    return api.post<ApiResponse<{
      file: DurableProjectFile;
      task: PersistedTaskSnapshot;
    }>>(`/api/project/${projectId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
  content: (projectId: number, fileId: number) =>
    projectApi.getFileContent(projectId, fileId),
  delete: (projectId: number, fileId: number) =>
    projectApi.deleteFile(projectId, fileId),
};

export const analysisApi = {
  get: (projectId: number) =>
    api.get<ApiResponse<DocumentAnalysisState>>(
      `/api/project/${projectId}/analysis`
    ),
  createFromFiles: (
    projectId: number,
    data: {
      source_file_ids: number[];
      base_project_version: number;
      idempotency_key: string;
    }
  ) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(`/api/project/${projectId}/analysis/tasks`, data),
  createFromPaste: (
    projectId: number,
    data: {
      pasted_text: string;
      base_project_version: number;
      idempotency_key: string;
    }
  ) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(`/api/project/${projectId}/paste-analysis`, data),
  update: (
    projectId: number,
    data: {
      base_analysis_version: number;
      overview: string;
      requirements: string;
      analysis_mode?: string;
    }
  ) =>
    api.put<ApiResponse<DocumentAnalysisSnapshot>>(`/api/project/${projectId}/analysis`, data),
  confirm: (projectId: number, baseAnalysisVersion: number) =>
    api.post<ApiResponse<DocumentAnalysisSnapshot>>(`/api/project/${projectId}/analysis/confirm`, {
      base_analysis_version: baseAnalysisVersion,
    }),
  switchMode: (
    projectId: number,
    data: {
      target_mode: 'file' | 'paste';
      confirm_switch: boolean;
      source_file_ids: number[];
      pasted_text: string;
      base_project_version: number;
      idempotency_key: string;
    }
  ) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(`/api/project/${projectId}/analysis-mode/switch`, data),
};

export const goodsMatchingApi = {
  get: (projectId: number) =>
    api.get<ApiResponse<GoodsMatchingState>>(
      `/api/project/${projectId}/goods-matching`
    ),
  createTask: (
    projectId: number,
    data: {
      base_analysis_version: number;
      idempotency_key: string;
      confirm_rematch: boolean;
    }
  ) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(`/api/project/${projectId}/goods-matching/tasks`, data),
  save: (
    projectId: number,
    data: {
      schema_version: 1;
      tender_items: GoodsRow[];
      company_items: GoodsRow[];
      matches: GoodsRow[];
      confirmed: boolean;
      base_analysis_version: number;
    }
  ) =>
    api.put<ApiResponse<Omit<GoodsMatchingState, 'task'>>>(`/api/project/${projectId}/goods-matching`, data),
};

export const taskApi = {
  get: (taskId: number) =>
    api.get<ApiResponse<PersistedTaskSnapshot>>(
      `/api/task/${taskId}`
    ),
  items: (taskId: number) =>
    api.get<ApiResponse<PersistedTaskItem[]>>(
      `/api/task/${taskId}/items`
    ),
  retryFailedItems: (taskId: number) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(`/api/task/${taskId}/retry-failed-items`),
  cancel: (taskId: number) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(`/api/task/${taskId}/cancel`),
  pause: (taskId: number) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(`/api/task/${taskId}/pause`),
  resume: (taskId: number) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(`/api/task/${taskId}/resume`),
  events: (
    taskId: number,
    lastEventId?: string,
    signal?: AbortSignal
  ) => {
    const token = getAccessToken();
    return fetch(`${API_BASE_URL}/api/task/${taskId}/events`, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(lastEventId ? { 'Last-Event-ID': lastEventId } : {}),
      },
      signal,
    });
  },
};

export const contentWorkspaceApi = {
  get: (projectId: number) =>
    api.get<ApiResponse<ContentDashboard>>(
      `/api/projects/${projectId}/content-dashboard`
    ),
  latestTask: (projectId: number) =>
    api.get<ApiResponse<PersistedTaskSnapshot | null>>(`/api/project/${projectId}/content/tasks/latest`),
  createBatchTask: (
    projectId: number,
    data: {
      base_project_version: number;
      base_outline_version: number;
      outline_ids: string[];
      chapter_references: ChapterReferenceSelection[];
      idempotency_key: string;
      writing_style?: string;
    }
  ) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(`/api/project/${projectId}/content/tasks`, data),
  createSingleTask: (
    projectId: number,
    outlineId: string,
    operationType: 'regenerate' | 'expand' | 'rewrite',
    data: {
      base_project_version: number;
      base_outline_version: number;
      base_content_version: number;
      instruction: string;
      chapter_reference: ChapterReferenceSelection | null;
      idempotency_key: string;
      writing_style?: string;
    }
  ) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(
      `/api/project/${projectId}/contents/${encodeURIComponent(outlineId)}/${operationType}`,
      data
    ),
  updateStyle: (projectId: number, style: string) =>
    api.put<ApiResponse<ContentDashboard>>(
      `/api/projects/${projectId}/content/style`,
      { writing_style: style }
    ),
  updateChapterConfig: (
    projectId: number,
    outlineId: string,
    data: { is_image_rich?: boolean; expected_word_count?: number }
  ) =>
    api.put<ApiResponse<ContentChapter>>(
      `/api/projects/${projectId}/contents/${encodeURIComponent(outlineId)}/config`,
      data
    ),
};

export const chapterReferenceApi = {
  list: (projectId: number, keyword = '') =>
    api.get<ApiResponse<ReferenceBid[]>>(
      `/api/projects/${projectId}/reference-bids`,
      { params: { keyword, limit: 50 } }
    ),
  preview: (
    projectId: number,
    fileId: number,
    offset = 0,
    limit = 6000
  ) =>
    api.get<ApiResponse<ReferenceBidPreview>>(
      `/api/projects/${projectId}/reference-bids/${fileId}/preview`,
      { params: { offset, limit } }
    ),
};

export const wordExportApi = {
  list: (projectId: number) =>
    api.get<ApiResponse<WordExportRecord[]>>(
      `/api/project/${projectId}/exports`
    ),
  create: (
    projectId: number,
    data: {
      base_project_version?: number;
      base_outline_version?: number;
      base_content_version?: number;
      export_type?: 'docx';
      idempotency_key?: string;
    } = {}
  ) =>
    api.post<ApiResponse<{
      export: WordExportRecord;
      task: PersistedTaskSnapshot;
    }>>(`/api/project/${projectId}/exports`, data),
  detail: (projectId: number, exportId: number) =>
    api.get<ApiResponse<{
      export: WordExportRecord;
      task: PersistedTaskSnapshot | null;
    }>>(`/api/project/${projectId}/exports/${exportId}`),
  download: (projectId: number, exportId: number) =>
    api.get<Blob>(
      `/api/project/${projectId}/exports/${exportId}/download`,
      { responseType: 'blob' }
    ),
  /** 获取或创建最新的 Word 导出（后端自动判断版本是否过期） */
  latest: (projectId: number) =>
    api.get<ApiResponse<{
      export: WordExportRecord | null;
      task: PersistedTaskSnapshot | null;
      action: 'up_to_date' | 'regenerated' | 'in_progress';
      reason: string;
    }>>(`/api/project/${projectId}/exports/latest`),
};

export const configApi = {
  saveConfig: (config: ConfigData) => api.post('/api/config/save', config),
  loadConfig: () => api.get<ApiResponse<ConfigData>>('/api/config/load'),
  getModels: (config: ConfigData) => api.post<ApiResponse<{ models: string[] }>>('/api/config/models', config),
  /** 获取可用正文风格（无需 project_id，从 bid_content_style 表读） */
  getAvailableContentStyles: () =>
    api.get<ApiResponse<Array<{ key: string; label: string; description?: string }>>>('/api/config/content-styles'),
};

export const documentApi = {
  uploadFile: (file: File, projectId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (projectId !== undefined) {
      formData.append('project_id', String(projectId));
    }
    return api.post<ApiResponse<{ file_content: string; old_outline?: string }>>('/api/document/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  analyzeDocumentStream: (data: AnalysisRequest) => postJson('/api/document/analyze-async-stream', data),
  exportWord: async (data: WordExportRequest) => ensureResponseOk(await postJson('/api/document/export-word', data), '导出失败'),
  exportTxt: async (data: { outline: OutlineItem[] }) => ensureResponseOk(await postJson('/api/document/export-txt', data), '导出失败'),
};

/** 异步流式分析任务状态 */
export interface StreamAnalysisTask {
  task_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  analysis_type: string;
  member_id: number;
  created_at: number;
  progress?: number;
  result_summary?: string;
}

export const streamAnalysisApi = {
  /** 查询任务状态 */
  getTask: (taskId: string) =>
    api.get<ApiResponse<StreamAnalysisTask>>(
      `/api/document/analyze-async-stream/tasks/${taskId}`
    ),
  /** 列出当前用户的任务 */
  listTasks: () =>
    api.get<ApiResponse<{
      total: number;
      tasks: StreamAnalysisTask[];
      active_count: number;
    }>>(
      '/api/document/analyze-async-stream/tasks'
    ),
  /** 取消任务 */
  cancelTask: (taskId: string) =>
    api.delete<ApiResponse<void>>(
      `/api/document/analyze-async-stream/tasks/${taskId}`
    ),
};

export const outlineWorkspaceApi = {
  get: (projectId: number) =>
    api.get<ApiResponse<OutlineWorkspaceState>>(
      `/api/projects/${projectId}/outline`
    ),
  save: (
    projectId: number,
    data: {
      base_project_version: number;
      outline: DurableOutlineNode[];
      confirm_content_impact: boolean;
      partial?: boolean;
    }
  ) =>
    api.put<ApiResponse<OutlineWorkspaceState>>(`/api/projects/${projectId}/outline`, data),
  /** 只替换指定章节节点的 children（AI 扩写/优化目录插入用，不用传完整目录树） */
  replaceNodeChildren: (
    projectId: number,
    outlineId: string,
    data: {
      base_project_version: number;
      children: DurableOutlineNode[];
      confirm_content_impact?: boolean;
    }
  ) =>
    api.put<ApiResponse<OutlineWorkspaceState>>(
      `/api/projects/${projectId}/outline/nodes/${outlineId}/children`,
      data
    ),
  confirm: (
    projectId: number,
    data: {
      base_project_version: number;
      outline_version: number;
    }
  ) =>
    api.post<ApiResponse<OutlineWorkspaceState>>(`/api/projects/${projectId}/outline/confirm`, data),
  versions: (projectId: number) =>
    api.get<ApiResponse<OutlineVersionRecord[]>>(
      `/api/projects/${projectId}/outline/versions`
    ),
  restore: (
    projectId: number,
    versionNo: number,
    data: {
      base_project_version: number;
      confirm_content_impact: boolean;
    }
  ) =>
    api.post<ApiResponse<OutlineWorkspaceState>>(
      `/api/projects/${projectId}/outline/versions/${versionNo}/restore`,
      data
    ),
  versionSnapshot: (projectId: number, versionNo: number) =>
    api.get<ApiResponse<{ version_no: number; source_type: string; snapshot: any }>>(
      `/api/projects/${projectId}/outline/versions/${versionNo}/snapshot`
    ),
  createCandidateTask: (
    projectId: number,
    data: {
      base_project_version: number;
      base_analysis_version: number;
      mode: OutlineMode;
      instruction: string;
      style: string;
      idempotency_key: string;
      single_candidate_id?: string;
    }
  ) =>
    api.post<ApiResponse<PersistedTaskSnapshot>>(`/api/projects/${projectId}/outline/candidate-tasks`, data),
  latestCandidates: (projectId: number) =>
    api.get<ApiResponse<PersistedTaskSnapshot | null>>(`/api/projects/${projectId}/outline/candidates`),
  applyCandidate: (
    projectId: number,
    candidateId: string,
    data: {
      task_id: number;
      base_project_version: number;
      base_analysis_version: number;
      confirm_content_impact: boolean;
    }
  ) =>
    api.post<ApiResponse<OutlineWorkspaceState>>(
      `/api/projects/${projectId}/outline/candidates/${encodeURIComponent(candidateId)}/apply`,
      data
    ),
  updateCandidateProfile: (
    projectId: number,
    candidateId: string,
    name: string
  ) =>
    api.patch<ApiResponse<Array<{ candidate_id: string; name: string; instruction: string }>>>(
      `/api/projects/${projectId}/outline/candidates/${encodeURIComponent(candidateId)}/profile`,
      { name }
    ),
  updateCandidateNodeTitle: (
    projectId: number,
    candidateId: string,
    nodeId: string,
    title: string
  ) =>
    api.post<ApiResponse<{
      candidate_id: string;
      node_id: string;
      title: string;
      outline: DurableOutlineNode[];
    }>>(
      `/api/projects/${projectId}/outline/candidates/${encodeURIComponent(candidateId)}/nodes/${encodeURIComponent(nodeId)}/title`,
      { title }
    ),
  operationTemplates: (projectId: number) =>
    api.get<ApiResponse<OutlineOperationTemplate[]>>(
      `/api/projects/${projectId}/outline/operation-templates`
    ),
  createOperation: (
    projectId: number,
    data: {
      base_project_version: number;
      base_outline_version: number;
      operation_type: 'optimize' | 'expand' | 'manual_idea';
      target_ids: string[];
      template_code: OutlineOperationTemplate['code'];
      instruction: string;
      style: string;
      idempotency_key: string;
    }
  ) =>
    api.post<ApiResponse<{
      operation: OutlineAIOperation;
      task: PersistedTaskSnapshot;
    }>>(`/api/projects/${projectId}/outline/operations`, data),
  operations: (projectId: number) =>
    api.get<ApiResponse<OutlineAIOperation[]>>(
      `/api/projects/${projectId}/outline/operations`,
      { params: { limit: 50 } }
    ),
  writingIdeaVersions: (
    projectId: number,
    outlineId?: string,
    limit: number = 50
  ) =>
    api.get<ApiResponse<
      {
        id: number;
        outline_id: string;
        outline_title: string;
        operation_type: string;
        operation_label: string;
        style: string;
        instruction: string;
        content: string;
        create_time: number;
      }[]
    >>(`/api/projects/${projectId}/outline/writing-idea/versions`, {
      params: { outline_id: outlineId, limit },
    }),
  optimizeIdea: (
    projectId: number,
    data: {
      node_id: string;
      current_idea: string;
      instruction?: string;
      style?: string;
      candidate_id?: string;
    }
  ) =>
    api.post<ApiResponse<{
      optimized_idea: string;
      node_id: string;
    }>>(`/api/projects/${projectId}/outline/idea-optimize`, data),
  generateWritingIdea: (
    projectId: number,
    data: {
      node_id: string;
      node_title: string;
      instruction?: string;
      style?: string;
      candidate_id?: string;
    }
  ) =>
    api.post<ApiResponse<{
      generated_idea: string;
      node_id: string;
    }>>(`/api/projects/${projectId}/outline/idea-generate`, data),
  applyOperation: (
    projectId: number,
    operationId: number,
    data: {
      base_project_version: number;
      base_outline_version: number;
      confirm_content_impact: boolean;
    }
  ) =>
    api.post<ApiResponse<OutlineWorkspaceState>>(
      `/api/projects/${projectId}/outline/operations/${operationId}/apply`,
      data
    ),
  saveMergedWritingIdea: (
    projectId: number,
    data: {
      base_project_version: number;
      outline_id: string;
      merged_content: string;
      style?: string;
      instruction?: string;
      create_version?: boolean;
      candidate_id?: string;
    }
  ) =>
    api.put<ApiResponse<OutlineWorkspaceState>>(
      `/api/projects/${projectId}/outline/writing-idea`,
      data
    ),
  applyWritingIdeas: (projectId: number) =>
    api.post<ApiResponse<OutlineWorkspaceState>>(
      `/api/projects/${projectId}/outline/writing-ideas/apply`
    ),
  saveOperationConfig: (
    projectId: number,
    data: {
      outline_id: string;
      operation_style: string;
      operation_instruction: string;
    }
  ) =>
    api.put<ApiResponse<{
      outline_id: string;
      operation_style: string;
      operation_instruction: string;
    }>>(
      `/api/projects/${projectId}/outline/operation-config`,
      data
    ),
  getOperationConfig: (projectId: number, outlineId: string) =>
    api.get<ApiResponse<{
      outline_id: string;
      operation_style: string;
      operation_instruction: string;
    }>>(
      `/api/projects/${projectId}/outline/operation-config/${outlineId}`
    ),
  getTemplates: (projectId: number, templateType?: string) =>
    api.get<ApiResponse<{
      style: TemplateItem[];
      instruction: TemplateItem[];
    }>>(
      templateType 
        ? `/api/projects/${projectId}/outline/templates?template_type=${templateType}`
        : `/api/projects/${projectId}/outline/templates`
    ),
  updateProjectCandidateProfile: (
    projectId: number,
    candidateId: string,
    data: {
      name?: string;
      instruction?: string;
      style?: string;
    }
  ) =>
    api.patch<ApiResponse<ProjectSettings>>(
      `/api/projects/${projectId}/outline/candidates/${encodeURIComponent(candidateId)}/profile`,
      data
    ),
};

export const outlineApi = {
  generateOutline: (data: OutlineRequest) => api.post<OutlineData>('/api/outline/generate', data),
  generateOutlineStream: (data: OutlineRequest) => postJson('/api/outline/generate-stream', data),
};

export const contentApi = {
  generateChapterContent: (data: ChapterContentRequest) => api.post<ApiResponse<{ content: string }>>('/api/content/generate-chapter', data),
  generateChapterContentStream: (data: ChapterContentRequest) => postJson('/api/content/generate-chapter-stream', data),
};

export const expandApi = {
  uploadExpandFile: (file: File, projectId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (projectId !== undefined) {
      formData.append('project_id', String(projectId));
    }
    return api.post<ApiResponse<{ file_content: string; old_outline?: string }>>('/api/expand/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000,
    });
  },
};

/** 清单快照类型 */
export type ListType = 'goods' | 'service' | 'bill_of_quantities';

export interface ListSnapshot {
  schema_version: 1;
  list_type: ListType;
  tender_items: GoodsRow[];
  company_items: GoodsRow[];
  matches: GoodsRow[];
  confirmed: boolean;
  base_analysis_version?: number | null;
  supplier_role?: 'dealer' | 'manufacturer' | null;
  plan_duration_days?: number | null;
  plan_duration_text?: string | null;
  source_file_ids?: number[];
  pasted_text?: string | null;
  [key: string]: unknown;
}

export interface ListSnapshotSaveRequest {
  list_snapshot: ListSnapshot;
  base_analysis_version: number;
  idempotency_key: string;
}

export interface ListSnapshotPasteRequest {
  base_analysis_version: number;
  pasted_text: string;
  idempotency_key: string;
}

export interface ListSnapshotState {
  list_snapshot: ListSnapshot | null;
  analysis_version: number;
}

export const listSnapshotApi = {
  /** 获取当前清单快照 */
  get: (projectId: number) =>
    api.get<ApiResponse<ListSnapshotState>>(
      `/api/project/${projectId}/list`
    ),
  /** 保存清单快照 */
  save: (projectId: number, data: ListSnapshotSaveRequest) =>
    api.post<ApiResponse<ListSnapshotState>>(
      `/api/project/${projectId}/list`,
      data
    ),
  /** 保存粘贴文本清单（后端解析，精简版） */
  saveFromPasted: (projectId: number, data: ListSnapshotPasteRequest) =>
    api.post<ApiResponse<ListSnapshotState>>(
      `/api/project/${projectId}/list/paste`,
      data
    ),
};

export default api;